import { formatDateInBogota, formatIsoDateInBogota } from './dateTime';

export const DATE_FILTER_FROM_PARAM = 'dateFrom';
export const DATE_FILTER_TO_PARAM = 'dateTo';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function normalizeDateFilterValue(value) {
  const normalized = String(value || '').trim();
  return ISO_DATE_PATTERN.test(normalized) ? normalized : '';
}

export function getDateFiltersFromSearchParams(searchParams) {
  return {
    dateFrom: normalizeDateFilterValue(searchParams.get(DATE_FILTER_FROM_PARAM)),
    dateTo: normalizeDateFilterValue(searchParams.get(DATE_FILTER_TO_PARAM))
  };
}

export function setDateFilterInSearchParams(searchParams, key, value) {
  const nextSearchParams = new URLSearchParams(searchParams);
  const normalizedValue = normalizeDateFilterValue(value);

  if (key !== DATE_FILTER_FROM_PARAM && key !== DATE_FILTER_TO_PARAM) {
    return nextSearchParams;
  }

  if (normalizedValue) {
    nextSearchParams.set(key, normalizedValue);
  } else {
    nextSearchParams.delete(key);
  }

  return nextSearchParams;
}

export function clearDateFiltersFromSearchParams(searchParams) {
  const nextSearchParams = new URLSearchParams(searchParams);
  nextSearchParams.delete(DATE_FILTER_FROM_PARAM);
  nextSearchParams.delete(DATE_FILTER_TO_PARAM);
  return nextSearchParams;
}

export function hasDateFilters(dateFilters) {
  return Boolean(dateFilters?.dateFrom || dateFilters?.dateTo);
}

export function getRecordCreatedDate(record) {
  return (
    record?.createdAt ??
    record?.created_at ??
    record?.createdDate ??
    record?.created_date ??
    ''
  );
}

export function getGroupFinalizedDate(group) {
  return group?.finalizedAt ?? group?.finalized_at ?? '';
}

export function getGroupStartDate(group) {
  return getGroupFirstMeetingDate(group) || getRecordCreatedDate(group);
}

export function isDateInRange(value, dateFilters) {
  if (!hasDateFilters(dateFilters)) {
    return true;
  }

  const recordDate = formatIsoDateInBogota(value);
  if (!recordDate) {
    return false;
  }

  if (dateFilters.dateFrom && recordDate < dateFilters.dateFrom) {
    return false;
  }

  if (dateFilters.dateTo && recordDate > dateFilters.dateTo) {
    return false;
  }

  return true;
}

export function filterRecordsByDateRange(records, dateFilters, getDateValue = getRecordCreatedDate) {
  const normalizedRecords = Array.isArray(records) ? records : [];

  if (!hasDateFilters(dateFilters)) {
    return normalizedRecords;
  }

  return normalizedRecords.filter(record =>
    isDateInRange(getDateValue(record), dateFilters)
  );
}

function getMeetingDateValue(meeting) {
  return meeting?.meetingDate ?? meeting?.meeting_date ?? '';
}

function getMeetingOrderValue(meeting) {
  const order = Number(meeting?.order);
  return Number.isInteger(order) && order > 0 ? order : null;
}

function getGroupProcessMeetings(group) {
  const meetingGroups = group?.meetingGroups ?? group?.meeting_groups ?? [];
  if (!Array.isArray(meetingGroups)) {
    return [];
  }

  return meetingGroups
    .filter(meeting => String(meeting?.type || '').trim().toUpperCase() === 'MEETING')
    .sort((left, right) => {
      const leftOrder = getMeetingOrderValue(left);
      const rightOrder = getMeetingOrderValue(right);

      if (leftOrder && rightOrder) {
        return leftOrder - rightOrder;
      }

      if (leftOrder) return -1;
      if (rightOrder) return 1;

      const leftDate = formatIsoDateInBogota(getMeetingDateValue(left));
      const rightDate = formatIsoDateInBogota(getMeetingDateValue(right));

      if (leftDate && rightDate) {
        return leftDate.localeCompare(rightDate);
      }

      if (leftDate) return -1;
      if (rightDate) return 1;

      return leftDate.localeCompare(rightDate);
    });
}

export function getGroupFirstMeetingDate(group) {
  const meetings = getGroupProcessMeetings(group);
  const firstOrderedMeeting = meetings.find(meeting => getMeetingOrderValue(meeting) === 1);

  if (firstOrderedMeeting) {
    return getMeetingDateValue(firstOrderedMeeting);
  }

  return getMeetingDateValue(meetings[0]);
}

export function filterSchoolsByGroupFirstMeetingDate(schools, dateFilters) {
  const normalizedSchools = Array.isArray(schools) ? schools : [];

  if (!hasDateFilters(dateFilters)) {
    return normalizedSchools;
  }

  return normalizedSchools.map(school => ({
    ...school,
    groups: filterRecordsByDateRange(
      school?.groups,
      dateFilters,
      getGroupFirstMeetingDate
    )
  }));
}

export function formatDateRangeLabel(dateFilters) {
  const fromLabel = dateFilters?.dateFrom
    ? formatDateInBogota(dateFilters.dateFrom)
    : '';
  const toLabel = dateFilters?.dateTo
    ? formatDateInBogota(dateFilters.dateTo)
    : '';

  if (fromLabel && toLabel) {
    return `Periodo filtrado: desde ${fromLabel} hasta ${toLabel}`;
  }

  if (fromLabel) {
    return `Periodo filtrado: desde ${fromLabel}`;
  }

  if (toLabel) {
    return `Periodo filtrado: hasta ${toLabel}`;
  }

  return 'Periodo filtrado: todo el historico';
}
